import React, { useState, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { JobApplication } from './ApplicationWizard'
import {
  communicationCoachService,
  CoachingContext,
  CoachingSession,
  ActionType,
  QualityTier,
  StoredCommunication,
  StoredCommunicationUpdate,
} from '../services/communicationCoachService'
import { CommunicationCoachWizard } from './CommunicationCoachWizard'
import { CommunicationCoachDisplay } from './CommunicationCoachDisplay'
import { StoredCommunicationsView } from './StoredCommunicationsView'
import './CommunicationCoachPanel.css'

interface CommunicationCoachPanelProps {
  application: JobApplication
  onClose: () => void
}

type PanelTab = 'new' | 'stored'

export const CommunicationCoachPanel: React.FC<CommunicationCoachPanelProps> = ({
  application,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<PanelTab>('new')
  const [currentStep, setCurrentStep] = useState<'wizard' | 'display'>('wizard')
  const [session, setSession] = useState<CoachingSession | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [storedCommunicationSessionId, setStoredCommunicationSessionId] = useState<string | null>(null)
  const [storedCommunicationEditMode, setStoredCommunicationEditMode] = useState(false)
  const [storedCommunicationsRefreshKey, setStoredCommunicationsRefreshKey] = useState(0)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  const handleWizardComplete = async (
    actionType: ActionType,
    input: string,
    context: CoachingContext,
    storeCommunication: boolean,
    qualityTier: QualityTier
  ) => {
    try {
      setError(null)
      if (!application.id) {
        throw new Error('Communication Coach requires a saved job application.')
      }

      let newSession: CoachingSession

      switch (actionType) {
        case 'email_response':
          newSession = await communicationCoachService.createEmailResponseSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'recruiter_outreach':
          newSession = await communicationCoachService.createRecruiterOutreachSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'cover_letter':
          newSession = await communicationCoachService.createCoverLetterSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'follow_up':
          newSession = await communicationCoachService.createFollowUpSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'thank_you_note':
          newSession = await communicationCoachService.createThankYouNoteSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'interview_confirmation':
          newSession = await communicationCoachService.createInterviewConfirmationSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'offer_response':
          newSession = await communicationCoachService.createOfferResponseSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'message_review':
          newSession = await communicationCoachService.createMessageReviewSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'writing_assistance':
          newSession = await communicationCoachService.createWritingAssistanceSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'legal_review':
          newSession = await communicationCoachService.createLegalReviewSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        case 'salary_negotiation':
          newSession = await communicationCoachService.createSalaryNegotiationSession(
            input,
            context,
            storeCommunication,
            application.id,
            qualityTier
          )
          break
        default:
          throw new Error('Invalid action type')
      }

      setSession(newSession)
      setStoredCommunicationSessionId(null)
      setStoredCommunicationEditMode(false)
      setCurrentStep('display')

      // Start polling if status is processing
      if (newSession.status === 'processing') {
        startPolling(newSession.session_id, actionType)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
    }
  }

  const startPolling = (sessionId: string, actionType: ActionType) => {
    setIsPolling(true)
    
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const updatedSession = await communicationCoachService.pollCoachingSession(sessionId, actionType)
        if (
          updatedSession &&
          (
            (updatedSession.status === 'completed' && updatedSession.result) ||
            updatedSession.status === 'failed'
          )
        ) {
          setSession(updatedSession)
          setIsPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }
      } catch (err) {
        console.error('Polling error:', err)
        setIsPolling(false)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    }, 3000) // Poll every 3 seconds
  }

  const handleBackToWizard = () => {
    setCurrentStep('wizard')
    setSession(null)
    setError(null)
    setStoredCommunicationSessionId(null)
    setStoredCommunicationEditMode(false)
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setIsPolling(false)
  }

  const handleDisplayBack = () => {
    if (storedCommunicationSessionId) {
      setActiveTab('stored')
      setCurrentStep('wizard')
      setSession(null)
      setError(null)
      setStoredCommunicationSessionId(null)
      setStoredCommunicationEditMode(false)
      return
    }

    handleBackToWizard()
  }

  const buildSessionFromStoredCommunication = (comm: StoredCommunication): CoachingSession => ({
    session_id: comm.session_id,
    job_application_id: comm.job_application_id,
    action_type: comm.action_type,
    input_content: comm.input_content,
    context: comm.context,
    result: comm.result,
    status: comm.status || 'completed',
    quality_tier: comm.quality_tier,
    model_used: comm.model_used,
    created_at: comm.created_at,
    updated_at: comm.updated_at,
  })

  const openStoredCommunication = (comm: StoredCommunication, editMode = false) => {
    setActiveTab('new')
    setCurrentStep('display')
    setSession(buildSessionFromStoredCommunication(comm))
    setStoredCommunicationSessionId(comm.session_id)
    setStoredCommunicationEditMode(editMode)
    setError(null)
  }

  const handleSaveStoredCommunication = async (updates: StoredCommunicationUpdate) => {
    if (!storedCommunicationSessionId) {
      throw new Error('Stored communication session is required')
    }

    const updatedCommunication = await communicationCoachService.updateStoredCommunication(
      storedCommunicationSessionId,
      updates,
    )

    setSession(buildSessionFromStoredCommunication(updatedCommunication))
    setStoredCommunicationEditMode(false)
    setStoredCommunicationsRefreshKey((value) => value + 1)
    toast.success('Stored communication updated')
  }

  const handleDeleteStoredCommunication = async (sessionIdOverride?: string) => {
    const sessionId = sessionIdOverride || storedCommunicationSessionId
    if (!sessionId) {
      throw new Error('Stored communication session is required')
    }

    await communicationCoachService.deleteStoredCommunication(sessionId)
    setStoredCommunicationsRefreshKey((value) => value + 1)
    if (storedCommunicationSessionId === sessionId) {
      setSession(null)
      setCurrentStep('wizard')
      setStoredCommunicationSessionId(null)
      setStoredCommunicationEditMode(false)
      setActiveTab('stored')
    }
    toast.success('Stored communication deleted')
  }

  // Handle escape key
  useEffect(() => {
    const shouldLockBodyScroll =
      typeof window !== 'undefined' && window.innerWidth <= 768
    const previousOverflow = document.body.style.overflow

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isMounted) {
      document.addEventListener('keydown', handleEscape)
      if (shouldLockBodyScroll) {
        document.body.style.overflow = 'hidden'
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [isMounted, onClose])

  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null
  }

  const panelContent = (
    <div className="communication-coach-slider-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    }}>
      <div className="communication-coach-slider" onClick={(e) => e.stopPropagation()}>
        <div className="communication-coach-slider-header">
          <div className="communication-coach-slider-title">
            <h2>Communication Coach</h2>
            {application.company && application.position && (
              <p className="communication-coach-slider-subtitle">
                {application.position} at {application.company}
              </p>
            )}
          </div>
          <div className="communication-coach-slider-header-actions">
            <button
              className="communication-coach-slider-close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="communication-coach-tabs">
          <button
            className={`communication-coach-tab ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('new')
              if (currentStep === 'display') {
                handleBackToWizard()
              }
            }}
          >
            New Communication
          </button>
          <button
            className={`communication-coach-tab ${activeTab === 'stored' ? 'active' : ''}`}
            onClick={() => setActiveTab('stored')}
          >
            Stored Communications
          </button>
        </div>

        <div className="communication-coach-slider-content">
          {activeTab === 'new' && (
            <>
              {error && (
                <div className="communication-coach-error">
                  <p>⚠️ {error}</p>
                  <button onClick={handleBackToWizard}>Try Again</button>
                </div>
              )}

              {currentStep === 'wizard' && !error && (
                <CommunicationCoachWizard
                  application={application}
                  onComplete={handleWizardComplete}
                  onCancel={onClose}
                />
              )}

              {currentStep === 'display' && session && (
                <CommunicationCoachDisplay
                  session={session}
                  isPolling={isPolling}
                  onBack={handleDisplayBack}
                  backLabel={storedCommunicationSessionId ? 'Back to Stored Communications' : 'Back to Wizard'}
                  canManageStoredCommunication={Boolean(storedCommunicationSessionId) && !isPolling}
                  initialEditMode={storedCommunicationEditMode}
                  onSaveStoredCommunication={handleSaveStoredCommunication}
                  onDeleteStoredCommunication={
                    storedCommunicationSessionId
                      ? () => handleDeleteStoredCommunication()
                      : undefined
                  }
                />
              )}
            </>
          )}

          {activeTab === 'stored' && (
            <StoredCommunicationsView
              application={application}
              onClose={() => {}} // No-op, we're inside the panel
              refreshKey={storedCommunicationsRefreshKey}
              onViewCommunication={(comm) => openStoredCommunication(comm, false)}
              onEditCommunication={(comm) => openStoredCommunication(comm, true)}
              onDeleteCommunication={(deletedSessionId) => {
                if (storedCommunicationSessionId === deletedSessionId) {
                  setSession(null)
                  setCurrentStep('wizard')
                  setStoredCommunicationSessionId(null)
                  setStoredCommunicationEditMode(false)
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  )

  return panelContent
}
